/**
 * @file Hook for managing board name editing state and operations
 * @remarks
 * Provides edit mode state, value tracking, and save/cancel handlers
 * for inline board name editing in the kanban top panel.
 */

import { useCallback, useState, useEffect } from "react";

/**
 * Props for the useBoardNameEditing hook
 * @property boardName - Current name of the board to display and edit
 * @property onUpdateBoardName - Async callback to persist board name changes to the server
 */
interface UseBoardNameEditingProps {
  readonly boardName?: string;
  readonly onUpdateBoardName?: (newName: string) => Promise<void>;
}

/**
 * Return type for the useBoardNameEditing hook
 * @property pipelineName - Current display name of the board
 * @property isEditing - Whether the board name is currently being edited
 * @property editValue - Current value in the edit input field
 * @property handleStartEdit - Initiates edit mode with current name
 * @property handleSaveEdit - Saves the edited name and exits edit mode
 * @property handleCancelEdit - Discards changes and exits edit mode
 * @property handleEditChange - Updates the edit input value
 */
interface UseBoardNameEditingReturn {
  readonly pipelineName: string;
  readonly isEditing: boolean;
  readonly editValue: string;
  readonly handleStartEdit: () => void;
  readonly handleSaveEdit: () => Promise<void>;
  readonly handleCancelEdit: () => void;
  readonly handleEditChange: (value: string) => void;
}

/**
 * Hook that manages board name editing functionality with edit/save/cancel operations.
 * @param props - Hook configuration
 * @param props.boardName - Current name of the board to display and edit
 * @param props.onUpdateBoardName - Async callback to persist board name changes to the server
 * @returns State and handlers for board name editing
 */
export const useBoardNameEditing = ({
  boardName = "Summer '25 Pipeline",
  onUpdateBoardName,
}: UseBoardNameEditingProps): UseBoardNameEditingReturn => {
  const [pipelineName, setPipelineName] = useState(boardName);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(boardName);

  const handleStartEdit = useCallback(() => {
    setEditValue(pipelineName);
    setIsEditing(true);
  }, [pipelineName]);

  const handleSaveEdit = useCallback(async () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== pipelineName) {
      // Optimistically update the UI before the network round-trip so that
      // the heading reflects the new name immediately on save click rather
      // than waiting for the Apollo mutation response to propagate through
      // the cache → useBoardManager sync chain.
      const previousName = pipelineName;
      setPipelineName(trimmedValue);
      setIsEditing(false);
      try {
        if (onUpdateBoardName) {
          await onUpdateBoardName(trimmedValue);
        }
      } catch (error) {
        console.error("Failed to update board name:", error);
        // Revert optimistic update on failure
        setPipelineName(previousName);
        setEditValue(previousName);
      }
      return;
    }
    if (!trimmedValue) {
      setEditValue(pipelineName);
    }
    setIsEditing(false);
  }, [editValue, pipelineName, onUpdateBoardName]);

  const handleCancelEdit = useCallback(() => {
    setEditValue(pipelineName);
    setIsEditing(false);
  }, [pipelineName]);

  const handleEditChange = useCallback((value: string) => {
    setEditValue(value);
  }, []);

  // Sync the displayed name from the upstream prop only when the prop
  // itself changes — NOT every time `isEditing` flips. The previous
  // combined effect ran on `setIsEditing(false)` after a successful save
  // and overwrote the just-set new name with whatever `boardName` prop
  // currently was; if `activeBoard.name` hadn't yet propagated the
  // mutation result through Apollo's normalized cache and the
  // `useBoardManager` sync effect, the local state reverted to the
  // pre-rename name and the test's `readActiveBoardTitle` poll saw
  // stale data for the rest of its budget.

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local state to an external boardName prop is the canonical "external-state mirror" effect.
    setPipelineName(boardName);
  }, [boardName]);

  // Snap the editor back to the upstream value any time the user is
  // *not* in edit mode. Splitting this from the pipelineName sync
  // means it can re-fire on `isEditing` toggles without disturbing
  // the optimistic display set in `handleSaveEdit`.
  useEffect(() => {
    if (!isEditing) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- snap-back is keyed off isEditing toggle; deriving in render would re-fire on every render.
      setEditValue(boardName);
    }
  }, [boardName, isEditing]);

  return {
    pipelineName,
    isEditing,
    editValue,
    handleStartEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleEditChange,
  };
};
